import { Link } from "react-router-dom";
import {
  MapPin, ShoppingBag, CalendarDays, ShieldCheck, Leaf, Truck, Sparkles,
  ArrowRight, ChevronRight, Star, Store,
} from "lucide-react";
import { BRAND, MENU_ITEMS, SIGNATURE_IDS } from "../lib/gomill-data";
import { Overline, Section, Pill } from "../components/Bits";
import FoodCard from "../components/FoodCard";

const FEATURES = [
  { icon: ShieldCheck, title: "Hygienic & Safe", desc: "Branded kitchens, daily checks, FSSAI compliant." },
  { icon: Leaf, title: "Millet-Led", desc: "Foxtail, jowar, ragi — wholesome and gut-friendly." },
  { icon: Truck, title: "Mobile Convenience", desc: "Trucks & kiosks near you, every hour of the day." },
  { icon: Sparkles, title: "Branded Trust", desc: "Backed by EBG Group. Built for long-term impact." },
];

const CATS = [
  { label: "Breakfast & Millet Classics", desc: "Idlis, dosas, poha — millet-led mornings.", image: "/menu/Masala%20Dosa.png", to: "/menu" },
  { label: "Rice & Millet Bowls", desc: "Wholesome, balanced & loaded.", image: "/menu/Dal%20Khichdi.png", to: "/menu" },
  { label: "Roti & Curry Combos", desc: "Soft phulkas with hearty curries.", image: "/menu/Matar%20Paneer%20Phulka.png", to: "/menu" },
  { label: "Pasta & Noodles", desc: "Comfort food, the millet way.", image: "/menu/Red%20Sauce%20Pasta.png", to: "/menu" },
];

const VS = {
  bad: [
    "Questionable hygiene",
    "Heavy & oily",
    "Random experience",
    "Guilt-inducing",
  ],
  good: [
    "Branded hygiene you can see",
    "No Reused Oil",
    "Same taste, every truck",
    "Wholesome satisfaction",
  ],
};

const HERO_STATS = [
  { icon: Leaf, value: "Freshly Prepared", label: "Daily" },
  { icon: Sparkles, value: "Millet-Based", label: "Menu" },
  { icon: ShieldCheck, value: "Hygienic Kitchen", label: "Standards" },
];

export default function Home() {
  const featured = SIGNATURE_IDS.map((id) => MENU_ITEMS.find((m) => m.id === id)).filter(Boolean);
  return (
    <>
      {/* ---------- HERO ---------- */}
      <section className="relative pt-10 md:pt-16 pb-24 px-6 md:px-12 overflow-hidden" data-testid="home-hero">
        {/* Textured backdrop layers */}
        <div aria-hidden className="absolute inset-0 -z-10 bg-gomill-cream gm-grain" />
        <div aria-hidden className="absolute -top-20 -left-32 -z-10 w-[420px] h-[420px] rounded-full bg-gomill-mustard/25 blur-3xl" />
        <div aria-hidden className="absolute top-40 right-0 -z-10 w-[520px] h-[520px] rounded-full bg-gomill-leaf/20 blur-3xl" />
        {/* Decorative giant outlined word */}
        <div aria-hidden className="hidden md:block absolute top-6 right-8 select-none pointer-events-none">
          <span className="font-serif italic text-[10rem] leading-none text-gomill-green/[0.05] tracking-tight">GoMill.</span>
        </div>
        {/* Dotted pattern strip */}
        <div aria-hidden className="absolute inset-x-0 top-1/2 h-px -z-10 gm-dashed opacity-30" />

        <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-6 gm-fade-up">
            <Overline className="mb-6">India's Guilt-Free Food on the Go</Overline>
            <h1 className="font-serif text-[44px] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-gomill-green font-bold">
              Street cravings,<br/>
              <span className="italic text-gomill-leaf">rebuilt</span> better.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gomill-muted max-w-xl leading-relaxed">
              Millet-led meals made fresh. Delivered fast by our mobile kitchens and kiosks across your city. {BRAND.tagline}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/menu"
                data-testid="hero-menu-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gomill-green text-white hover:bg-gomill-leaf font-semibold transition-colors"
              >
                <ShoppingBag className="w-4 h-4" /> Browse Menu
              </Link>
              <Link
                to="/find"
                data-testid="hero-find-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gomill-mustard text-gomill-green hover:bg-gomill-mustardDark font-semibold transition-colors"
              >
                <MapPin className="w-4 h-4" /> Find GoMill
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-2.5 sm:gap-3 max-w-xl">
              {HERO_STATS.map((s) => (
                <div
                  key={s.value}
                  className="group inline-flex items-center gap-2.5 bg-white border border-gomill-border rounded-full pl-2 pr-4 py-2 shadow-sm hover:shadow-md hover:border-gomill-mustard transition-all"
                >
                  <span className="w-8 h-8 rounded-full bg-gomill-green text-gomill-mustard flex items-center justify-center shrink-0">
                    <s.icon className="w-4 h-4" />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="text-sm sm:text-[15px] font-bold text-gomill-green">{s.value}</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-gomill-muted">{s.label}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            {/* Hero visual: full image, clean */}
            <div className="relative">
              <div className="relative rounded-[36px] overflow-hidden aspect-[3/2] shadow-[0_30px_60px_-20px_rgba(15,56,36,0.4)] bg-gomill-cream">
                <img
                  src={BRAND.heroFood}
                  alt="GoMill food spread with truck and kiosk"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>

              {/* Floating brand cards */}
              <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-white rounded-2xl shadow-xl px-5 py-4 border border-gomill-border">
                <div className="w-10 h-10 rounded-full bg-gomill-mustard/30 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-gomill-green" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gomill-leaf font-semibold">FSSAI Certified</div>
                  <div className="font-serif text-lg text-gomill-green leading-tight">100% Millet-led</div>
                </div>
              </div>
              <div className="absolute -top-5 right-6 hidden md:flex items-center gap-2 bg-gomill-mustard text-gomill-green rounded-full px-4 py-2 shadow-lg">
                <Star className="w-4 h-4 fill-gomill-green" />
                <span className="text-sm font-semibold">Serving Healthy Millet Meals</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- MARQUEE ---------- */}
      <div className="bg-gomill-green text-white py-5 overflow-hidden border-y border-gomill-leaf">
        <div className="flex gm-marquee whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10 px-6 shrink-0">
              {["HYGIENIC", "MILLET-LED", "MOBILE", "TRUSTED", "FAST", "WHOLESOME", "EVERYWHERE", "GUILT-FREE"].map((w) => (
                <span key={w + i} className="font-serif text-3xl md:text-4xl tracking-wider flex items-center gap-10">
                  {w}
                  <Wheat />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ---------- HUNGRY NOW (3 cards on green block) ---------- */}
      <Section className="!py-12 md:!py-20">
        <div className="relative bg-gomill-green text-white rounded-[40px] overflow-hidden px-6 md:px-12 py-16 md:py-20">
          {/* Decorative bg */}
          <div aria-hidden className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gomill-mustard/15 blur-3xl" />
          <div aria-hidden className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-gomill-leaf/30 blur-3xl" />
          <svg aria-hidden className="absolute inset-0 w-full h-full opacity-[0.06]" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="dots-2" width="14" height="14" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.2" fill="#E5B03C" />
              </pattern>
            </defs>
            <rect width="200" height="200" fill="url(#dots-2)" />
          </svg>

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-gomill-mustard">
                <span className="w-2 h-2 rounded-full bg-gomill-mustard animate-pulse" /> Hungry now?
              </span>
              <h2 className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                Find the nearest <span className="italic">GoMill.</span>
              </h2>
              <p className="mt-5 text-white/75 text-lg max-w-md">
                Clean food. Trusted brand. Always near you. Pick how you'd like to GoMill today.
              </p>
              <Link to="/find" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gomill-mustard text-gomill-green font-bold hover:bg-gomill-mustardDark transition-colors" data-testid="hungry-cta">
                Locate near me <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: MapPin, title: "Locate GoMill", desc: "Live trucks & kiosks near you.", to: "/find", cta: "Locate now" },
                { icon: Store, title: "Franchise Inquiry", desc: "Own a GoMill kiosk, food truck, or outlet in your city.", to: "/franchise", cta: "Explore Franchise" },
                { icon: CalendarDays, title: "Book an Event", desc: "Bring GoMill to your community.", to: "/book-event", cta: "Book now" },
              ].map((c) => (
                <Link
                  key={c.title}
                  to={c.to}
                  data-testid={`home-card-${c.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="group relative bg-white/5 hover:bg-white border border-white/10 hover:border-gomill-mustard rounded-3xl p-6 transition-all duration-300 hover:-translate-y-1 flex flex-col"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gomill-mustard text-gomill-green flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <c.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-2xl text-white group-hover:text-gomill-green leading-tight">{c.title}</h3>
                  <p className="text-white/65 group-hover:text-gomill-muted text-sm mt-2">{c.desc}</p>
                  <div className="mt-auto pt-6 inline-flex items-center gap-1 text-sm font-semibold text-gomill-mustard group-hover:text-gomill-green">
                    {c.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- HEALTHY CRAVINGS (image-driven category teaser) ---------- */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <Overline className="mb-3">Healthy cravings?</Overline>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-gomill-green leading-[1.05]">
              We've got <span className="italic">every</span> craving covered.
            </h2>
          </div>
          <div className="md:col-span-5 md:text-right">
            <p className="text-gomill-muted text-lg max-w-md md:ml-auto">From wholesome millet bowls to freshly prepared meals — every dish is crafted for the way India chooses to eat today.</p>
            <Link to="/menu" className="mt-5 inline-flex items-center gap-2 text-gomill-green font-semibold hover:text-gomill-leaf" data-testid="home-explore-menu">
              Explore Menu <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {CATS.map((c) => (
            <Link
              to={c.to}
              key={c.label}
              data-testid={`home-cat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group relative aspect-[4/5] rounded-3xl overflow-hidden border border-gomill-border bg-gomill-cream"
            >
              <img src={c.image} alt={c.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-gomill-green/85 via-gomill-green/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-white">
                <div className="font-serif text-xl md:text-2xl leading-tight">{c.label}</div>
                <p className="text-white/80 text-sm mt-1">{c.desc}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-gomill-mustard group-hover:translate-x-1 transition-transform">
                  See dishes <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* ---------- VS COMPARISON (modern compact) ---------- */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <Overline className="mb-4">The GoMill Difference</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-[1.05]">All the craving. <span className="italic">None</span> of the compromise.</h2>
            <p className="mt-4 text-gomill-muted">India loves street food. But hygiene, consistency and guilt often come in the way. GoMill changes that — one branded kitchen, one millet meal at a time.</p>
            <div className="mt-6 flex items-center gap-6">
              <div>
                <div className="font-serif text-2xl md:text-3xl text-gomill-green leading-tight">Millet-Centric</div>
                <div className="text-xs uppercase tracking-widest text-gomill-muted mt-1">Menu</div>
              </div>
              <div className="w-px h-12 bg-gomill-border" />
              <div>
                <div className="font-serif text-2xl md:text-3xl text-gomill-green leading-tight">Hygienic</div>
                <div className="text-xs uppercase tracking-widest text-gomill-muted mt-1">Kitchen Standards</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-gomill-border bg-white overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_1fr] text-xs uppercase tracking-[0.18em] font-bold">
                <div className="px-5 py-4 text-gomill-muted">Outside food today</div>
                <div className="px-3 py-4 text-center text-gomill-muted">vs</div>
                <div className="px-5 py-4 bg-gomill-green text-gomill-mustard">The GoMill way</div>
              </div>
              {VS.bad.map((b, i) => (
                <div key={i} className={`grid grid-cols-[1fr_auto_1fr] items-center border-t border-gomill-border ${i % 2 ? "bg-gomill-cream/40" : ""}`}>
                  <div className="px-5 py-4 flex items-center gap-3 text-gomill-text">
                    <span className="w-6 h-6 rounded-full bg-gomill-text/10 text-gomill-text/70 flex items-center justify-center text-xs shrink-0">×</span>
                    <span className="text-sm md:text-base">{b}</span>
                  </div>
                  <div className="px-3 py-4 text-gomill-border">→</div>
                  <div className="px-5 py-4 flex items-center gap-3 text-gomill-green font-medium bg-gomill-green/[0.02]">
                    <span className="w-6 h-6 rounded-full bg-gomill-mustard text-gomill-green flex items-center justify-center text-xs font-bold shrink-0">✓</span>
                    <span className="text-sm md:text-base">{VS.good[i]}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- BENEFITS (clean inline row) ---------- */}
      <Section className="!pt-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 md:gap-x-10 gap-y-10 border-t border-gomill-border pt-12">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-gomill-mustard/25 text-gomill-green flex items-center justify-center shrink-0">
                <f.icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-serif text-lg md:text-xl text-gomill-green leading-tight">{f.title}</h3>
                <p className="text-sm text-gomill-muted mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- FEATURED MENU ---------- */}
      <Section>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <Overline className="mb-3">Signature Picks</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight max-w-xl">
              Street cravings, <span className="italic">rebuilt</span> better.
            </h2>
          </div>
          <Link to="/menu" className="inline-flex items-center gap-2 text-gomill-green font-semibold hover:text-gomill-leaf" data-testid="home-browse-menu">
            Browse Full Menu <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featured.map((m) => <FoodCard key={m.id} item={m} />)}
        </div>
      </Section>

      {/* ---------- HOW IT WORKS (modern horizontal stepper) ---------- */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10">
          <div className="lg:col-span-7">
            <Overline className="mb-4">The GoMill Experience</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-[1.05]">From craving to plate,<br className="hidden md:block"/> in four <span className="italic">simple</span> steps.</h2>
          </div>
          <p className="lg:col-span-5 text-gomill-muted">Designed for the way India moves — order in seconds, pick up hot in minutes, and make it part of your daily routine.</p>
        </div>

        <div className="relative">
          {/* Dashed connector */}
          <div aria-hidden className="hidden md:block absolute top-7 left-[12%] right-[12%] border-t-2 border-dashed border-gomill-mustard/50" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-4 md:gap-x-6 relative">
            {[
              { n: "01", t: "Order", d: "On app, WhatsApp or at the truck." },
              { n: "02", t: "Pick up", d: "Live-cooked, hot, ready in minutes." },
              { n: "03", t: "Eat", d: "Wholesome, millet-led, deeply tasty." },
              { n: "04", t: "Repeat", d: "Daily plans available for offices." },
            ].map((s) => (
              <div key={s.n} className="text-center md:text-left flex md:block flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-gomill-green text-gomill-mustard font-serif text-xl flex items-center justify-center relative z-10 border-4 border-gomill-bg shadow-sm">
                  {s.n}
                </div>
                <div className="mt-5">
                  <div className="font-serif text-2xl text-gomill-green">{s.t}</div>
                  <p className="text-sm text-gomill-muted mt-2 max-w-[200px] mx-auto md:mx-0">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ---------- BOTTOM CTA ---------- */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center bg-gomill-mustard rounded-[36px] p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/30" />
          <div className="md:col-span-7 relative">
            <Overline className="mb-3">Bring GoMill to you</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">Hosting an event? Let's GoMill it.</h2>
            <p className="mt-4 text-gomill-green/80 text-lg max-w-xl">From college lawns to corporate parks and community events — we serve millet meals people love.</p>
          </div>
          <div className="md:col-span-5 flex md:justify-end gap-3 relative">
            <Link to="/book-event" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gomill-green text-white hover:bg-gomill-leaf font-semibold" data-testid="home-cta-book">
              Book for Event
            </Link>
            <Link to="/franchise" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-gomill-green hover:bg-gomill-bg font-semibold" data-testid="home-cta-franchise">
              Franchise <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}

// Local wheat icon component inline (so home file is self-contained)
function Wheat({ className = "w-5 h-5 text-gomill-mustard" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 22V12"/>
      <path d="M12 12c-3 0-5-2-5-5 3 0 5 2 5 5z"/>
      <path d="M12 12c3 0 5-2 5-5-3 0-5 2-5 5z"/>
      <path d="M12 7c-3 0-5-2-5-5 3 0 5 2 5 5z"/>
      <path d="M12 7c3 0 5-2 5-5-3 0-5 2-5 5z"/>
    </svg>
  );
}
